import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import HoverHelp from '../../../common/HoverHelp';
import useToggle from '../../../common/hooks/useToggle';

import ReportSkeleton from '../ReportSkeleton';
import getEmployeeProjectFilters from '../EmployeeProjectFilters';

import {
  filterOnCols,
  isValidTaskHelper,
  getInitialFilters,
  getCostcodeInitialOptions,
} from '../../reportHelpers';
import useReportCostcodes from '../../useReportCostcodes';
import { searchCostcodeOptions } from '../../../costcodes/state/costcodes.actions';

import { getIdMap } from '../../../helpers/helpers';

import {
  computeData,
  getAllColumns,
  getColFilters,
} from './employeePayroll.helpers';
import {
  DEFAULT_ENABLED_FILTERS,
  DEFAULT_WORK_TYPE,
  SUMMARY_EXCLUDE_DATA,
  TYPE_MAP,
} from './employeePayroll.constants';

import Debouncer from '../../../helpers/Debouncer';
import { formatProjectLabelFromCompanySettings } from '../../../projects/projectHelpers';
import useEntityOTProfileMap from '../../../common/hooks/useEntityOTProfileMap';

const debouncer = new Debouncer();

function EmployeePayroll({
  renderColFilters,
  renderRowFilters,
  columns,
  users,
  projects,
  costcodes,
  phases,
  allProjects,
  allPhases,
  allUsers,
  selectAllUsers,
  selectAllProjects,
  selectAllPhases,
  selectAllCostcodes,
  hasNullProject,
  hasNullPhase,
  hasNullCostcode,
  onColumnsChanged,
  zeroHoursEnabled: propZeroHoursEnabled,
  onZeroHoursChanged,
  onSummaryChanged,
  height,
  onDataChanged,
  onFiltersChanged,
  settings,
  isRounded,
  userDivisions,
  timeEntryUserMap,
  dateRange,
  classMap,
  divisions,
  onRef,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const userIds = useMemo(() => Object.keys(timeEntryUserMap).filter(Boolean), [timeEntryUserMap]);
  const divisionIds = useMemo(() => Array.from(userDivisions), [userDivisions]);

  const entityOTProfileMap = useEntityOTProfileMap({
    userIds,
    divisionIds,
  });
  const allColumns = useMemo(
    () => getAllColumns(entityOTProfileMap, settings),
    [entityOTProfileMap, settings],
  );

  const [checkedColumns, setCheckedColumns] = useState(
    new Set(allColumns.map((col) => col.key).filter(filterOnCols(columns))),
  );
  const [stateColumns, setStateColumns] = useState(
    allColumns.filter((col) => checkedColumns.has(col.key)),
  );
  const { isToggled: showActiveUsersOnly, toggle: onActiveUsersToggle } = useToggle(false);
  const [filters, setFilters] = useState(getInitialFilters({
    selectAllUsers,
    allUsers,
    users,
    selectAllProjects,
    allProjects,
    projects,
    selectAllCostcodes,
    costcodes,
    selectAllPhases,
    allPhases,
    phases,
    settings,
    hasNullProject,
    hasNullPhase,
    hasNullCostcode,
  }));
  const [enabledFilters, setEnabledFilters] = useState(DEFAULT_ENABLED_FILTERS);
  const [summaryEnabled, setSummaryEnabled] = useState(!!stateColumns?.includes('summaryEnabled'));
  const [workType, setWorkType] = useState(DEFAULT_WORK_TYPE);
  const [zeroHoursEnabled, setZeroHoursEnabled] = useState(propZeroHoursEnabled);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtersChanged, setFiltersChanged] = useState(false);

  const projectIdMap = useMemo(() => getIdMap(allProjects), [allProjects]);
  const phaseMap = useMemo(() => getIdMap(allPhases), [allPhases]);
  const costcodeIdMap = useReportCostcodes(timeEntryUserMap);
  const divisionMap = useMemo(() => getIdMap(divisions), [divisions]);

  const onZeroHoursToggle = useCallback(() => {
    setZeroHoursEnabled(!zeroHoursEnabled);
    onZeroHoursChanged(!zeroHoursEnabled);
  }, [zeroHoursEnabled]);

  const toggleFilter = useCallback((item, checked) => {
    const {
      key: itemKey,
    } = item;

    const newChecked = new Set(checkedColumns);

    if (checked) {
      newChecked.add(itemKey);
    } else {
      newChecked.delete(itemKey);
    }
    const newCols = allColumns.filter((col) => newChecked.has(col.key));
    setStateColumns(newCols);
    setCheckedColumns(newChecked);
  }, [
    checkedColumns,
    allColumns,
  ]);

  const updateRowFilters = useCallback((key, checked) => {
    setEnabledFilters({
      ...enabledFilters,
      [key]: checked,
    });
    if (SUMMARY_EXCLUDE_DATA.includes(key) && checked) {
      setSummaryEnabled(false);
    }
    toggleFilter({ key }, checked);
  }, [enabledFilters, toggleFilter]);

  const onSummaryToggle = useCallback(() => {
    SUMMARY_EXCLUDE_DATA.forEach((key) => updateRowFilters(key, summaryEnabled));
    setSummaryEnabled(!summaryEnabled);
    onSummaryChanged(!summaryEnabled);
  }, [updateRowFilters, summaryEnabled, onSummaryChanged]);

  const isValidTask = useCallback(isValidTaskHelper({
    dateRange,
    filters,
    projectIdMap,
    costcodeIdMap,
    phaseMap,
    checkPhase: true,
    settings,
  }), [
    dateRange,
    filters,
    projectIdMap,
    costcodeIdMap,
    phaseMap,
    settings,
  ]);

  const getAggregateKey = useCallback((
    projectName,
    phaseName,
    costcodeName,
    serviceLocationText,
    taskId,
  ) => {
    const {
      date,
      costcode,
      phase,
      project,
      serviceLocation,
    } = enabledFilters;
    if (summaryEnabled) return 'All';
    const keys = [projectName, phaseName, costcodeName, serviceLocationText, taskId];
    const taskFilters = [date, serviceLocation, costcode, phase, project];
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const index in taskFilters) {
      const filter = taskFilters[index];
      if (filter) return keys.join('-');
      keys.pop();
    }
    return 'All';
  }, [
    summaryEnabled,
    enabledFilters,
    filters,
  ]);

  const colFilters = useMemo(() => (
    getColFilters(checkedColumns, settings)
  ), [checkedColumns, settings]);

  const costcodeIdsKey = useMemo(
    () => (costcodes || []).map((cc) => cc?.id).filter(Boolean).sort().join(','),
    [costcodes],
  );

  // base off key so it re-runs when actual costcodes change instead of just the reference
  const costcodeInitialOptions = useMemo(
    () => getCostcodeInitialOptions(costcodes),
    [costcodeIdsKey],
  );

  const rowFilters = useMemo(() => {
    const relevantPhases = filters.projects?.size === 0
      ? allPhases
      : allPhases.filter((phase) => {
        const project = projectIdMap[phase.projectId];
        if (!project) return false;
        const projectLabel = formatProjectLabelFromCompanySettings({
          name: project.name,
          number: project.number,
          settings,
        });
        return filters.projects.has(projectLabel);
      });

    const rfs = getEmployeeProjectFilters({
      onFilterToggle: (type, checked) => {
        setFiltersChanged(true);
        if (type === 'users') {
          setFilters({
            ...filters,
            users: allUsers.filter((user) => checked.has(user.name)),
          });
          return;
        }
        if (type === 'workType') {
          const newWorkType = new Set();
          Array.from(checked).forEach((checkedType) => {
            TYPE_MAP[checkedType]?.forEach((newType) => {
              newWorkType.add(newType);
            });
          });
          setWorkType(newWorkType);
          return;
        }
        setFilters({
          ...filters,
          [type]: checked,
        });
      },
      onCheckChanged: (type, checked) => updateRowFilters(type, checked),
      users: allUsers,
      projects: allProjects,
      phases: relevantPhases,
      costcodeFetchOptions: (params) => dispatch(searchCostcodeOptions(params)),
      costcodeInitialOptions,
      costcodeProjects: allProjects,
      costcodePhases: allPhases,
      checkedColumns,
      showWorkType: true,
      showBreak: true,
      showServiceLocations: true,
      settings,
      t,
      filters,
      showActiveUsersOnly,
      onActiveUsersToggle,
    });

    rfs.push({
      title: 'Date',
      key: 'date',
      onCheckChanged: (checked) => updateRowFilters('date', checked),
      checked: checkedColumns.has('date'),
    });

    if (settings.enableTimecardSubmission) {
      rfs.push({
        title: 'Approved',
        key: 'approved',
        onCheckChanged: (checked) => updateRowFilters('approved', checked),
        checked: checkedColumns.has('approved'),
      });
      rfs.push({
        title: 'Unapproved',
        key: 'unapproved',
        onCheckChanged: (checked) => updateRowFilters('unapproved', checked),
        checked: checkedColumns.has('unapproved'),
      });
    }

    return rfs;
  }, [
    checkedColumns,
    users,
    projects,
    phases,
    costcodeInitialOptions,
    allProjects,
    allPhases,
    allUsers,
    projectIdMap,
    filters,
    updateRowFilters,
    settings,
    showActiveUsersOnly,
    onActiveUsersToggle,
  ]);

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      const newData = await debouncer.debounce(() => (
        computeData({
          ...filters,
          onDataChanged,
          onFiltersChanged,
          settings,
          isRounded,
          userDivisions,
          timeEntryUserMap,
          enabledFilters,
          workType,
          zeroHoursEnabled,
          isValidTask,
          getAggregateKey,
          divisionMap,
          projectIdMap,
          costcodeIdMap,
          classIdMap: classMap,
          phaseMap,
          dateRange,
          showActiveUsersOnly,
          entityOTProfileMap,
        })
      ), 200);
      setLoading(false);
      setData(newData);
    };
    getData();
  }, [
    onDataChanged,
    onFiltersChanged,
    settings,
    isRounded,
    userDivisions,
    timeEntryUserMap,
    enabledFilters,
    workType,
    zeroHoursEnabled,
    isValidTask,
    getAggregateKey,
    divisionMap,
    projectIdMap,
    costcodeIdMap,
    classMap,
    phaseMap,
    filters,
    dateRange,
    showActiveUsersOnly,
    entityOTProfileMap,
  ]);

  useEffect(() => {
    if (filtersChanged) return;
    setFilters(getInitialFilters({
      selectAllUsers,
      allUsers,
      users,
      selectAllProjects,
      allProjects,
      projects,
      selectAllCostcodes,
      costcodes,
      selectAllPhases,
      allPhases,
      phases,
      hasNullProject,
      hasNullPhase,
      hasNullCostcode,
      settings,
    }));
  }, [
    users,
    allUsers,
    projects,
    allProjects,
    phases,
    allPhases,
    costcodeIdsKey,
    selectAllCostcodes,
    settings,
    hasNullProject,
    hasNullPhase,
    hasNullCostcode,
    filtersChanged,
  ]);

  useEffect(() => {
    onRef({
      onFilterToggle: toggleFilter,
    });
  }, [onRef, toggleFilter]);

  useEffect(() => {
    onSummaryChanged(summaryEnabled);
    onZeroHoursChanged(zeroHoursEnabled);
  }, [
    onSummaryChanged,
    onZeroHoursChanged,
  ]);

  useEffect(() => {
    onColumnsChanged(stateColumns);
  }, [
    stateColumns,
  ]);

  useEffect(() => {
    const newCheckedColumns = new Set(
      allColumns.map((col) => col.key).filter(filterOnCols(columns)),
    );
    setCheckedColumns(newCheckedColumns);
    setStateColumns(allColumns.filter((col) => newCheckedColumns.has(col.key)));
  }, [allColumns, columns]);

  return (
    <ReportSkeleton
      useVirtualization
      onDataChanged={onDataChanged}
      data={data}
      columns={stateColumns}
      height={height}
      loading={loading}
      colFilters={renderColFilters(colFilters)}
      rowFilters={renderRowFilters(rowFilters)}
      summaryConfig={{
        checked: summaryEnabled,
        onCheckChanged: onSummaryToggle,
      }}
      zeroHoursConfig={{
        checked: zeroHoursEnabled,
        onCheckChanged: onZeroHoursToggle,
        hoverHelp: (
          <HoverHelp
            placement="topRight"
            content={(
              <div style={{ width: 250 }}>
                Check this box to show all the users with zero time cards.
              </div>
            )}
          />
        ),
      }}
    />
  );
}

/* eslint-disable react/forbid-prop-types */
EmployeePayroll.propTypes = {
  renderColFilters: PropTypes.func.isRequired,
  renderRowFilters: PropTypes.func.isRequired,
  columns: PropTypes.array,
  users: PropTypes.array,
  projects: PropTypes.array,
  phases: PropTypes.array,
  costcodes: PropTypes.array,
  allProjects: PropTypes.array,
  allPhases: PropTypes.array,
  allUsers: PropTypes.array,
  selectAllUsers: PropTypes.bool,
  selectAllProjects: PropTypes.bool,
  selectAllPhases: PropTypes.bool,
  selectAllCostcodes: PropTypes.bool,
  onColumnsChanged: PropTypes.func.isRequired,
  zeroHoursEnabled: PropTypes.bool,
  onZeroHoursChanged: PropTypes.func.isRequired,
  onSummaryChanged: PropTypes.func.isRequired,
  height: PropTypes.number,
  onDataChanged: PropTypes.func.isRequired,
  onFiltersChanged: PropTypes.func.isRequired,
  settings: PropTypes.object,
  isRounded: PropTypes.bool,
  userDivisions: PropTypes.object,
  timeEntryUserMap: PropTypes.object,
  dateRange: PropTypes.array.isRequired,
  classMap: PropTypes.object,
  divisions: PropTypes.array,
  onRef: PropTypes.func.isRequired,
};

EmployeePayroll.defaultProps = {
  columns: [],
  users: [],
  projects: [],
  phases: [],
  costcodes: [],
  allProjects: [],
  allPhases: [],
  allUsers: [],
  selectAllUsers: false,
  selectAllProjects: false,
  selectAllPhases: false,
  selectAllCostcodes: false,
  zeroHoursEnabled: false,
  height: 450,
  settings: {},
  isRounded: false,
  userDivisions: {},
  timeEntryUserMap: {},
  classMap: {},
  divisions: [],
};

export default EmployeePayroll;
